import { useEffect, useMemo } from 'react';
import { fCurrency } from '../../utils/formatNumber';
import { useRadioGroup } from '../../hooks/useRadioGroup';
import { PricingCalculator, type PricingDiscountRule } from '../../utils/pricingCalculator';
import type { TestProps } from '../../models';

interface PaymentOptionSelectorProps {
	tests: TestProps[];
	onChange: (amount: number, type: 'full' | 'split') => void;
	discountRules?: PricingDiscountRule[];
}

export const PaymentOptionSelector = ({ tests, onChange, discountRules = [] }: PaymentOptionSelectorProps) => {
	const price = useMemo(() => new PricingCalculator(tests, discountRules), [tests, discountRules]);
	const total = useMemo(() => price.total(), [price]);
	const deposit = useMemo(() => price.deposit(), [price]);
	const balance = useMemo(() => price.balance(), [price]);
	const depositWithoutGST = useMemo(() => price.depositWithoutGST(), [price]);
	const totalWithoutGST = useMemo(() => price.totalWithoutGST(), [price]);

	const { RadioGroup: PaymentOptions, value } = useRadioGroup({
		options: [
			{
				label: `Pay ${fCurrency(Number(total))} now`,
				value: 'full',
			},
			{
				label: 'Pay 30% now, 70% later',
				subtitle: `Pay ${fCurrency(Number(deposit))} now, ${fCurrency(Number(balance))} after test completion.`,
				value: 'split',
			},
		],
	});

	useEffect(() => {
		const amount = value === 'full' ? totalWithoutGST : depositWithoutGST;
		onChange(amount, value as 'full' | 'split');
	}, [value, depositWithoutGST, totalWithoutGST, onChange]);

	return <PaymentOptions />;
};
